import { Box, Grid, Tab, Tabs, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';

import { getUserEdit, getUsersInfo } from '../../../../packages/common/api';
import Competencies from '../../../../packages/common/shared-ui/src/components/Competencies/Competencies';
import { ButtonsTypes } from '../../../../packages/common/shared-ui/src/enums/commonEnums';
import { formatFields } from '../../../../packages/common/utils/fieldHelper';
import { useTranslation } from '../../../../packages/common/utils/getLocalString';
import CommonPage from '../../CommonPage/CommonPage';

import UserPageCommonTab from './components/UserPageCommonTab/UserPageCommonTab';
import UserPageCompetencyTab from './components/UserPageCompetencyTab/UserPageRatesTab';
import UserPageRatesTab from './components/UserPageRatesTab/UserPageRatesTab';

// Original class: userTabs
const StyledUserTabs = styled(Tabs)(theme => ({
  '&.MuiTabs-root': {
    minHeight: 'auto',
  },

  '& .MuiButtonBase-root.MuiTab-root': {
    color: '#656985',
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: '400',
    padding: '8px 16px',
    textTransform: 'capitalize',
    borderRadius: '16px 0 0 16px',
    backgroundColor: '#DCDFF4',
    minHeight: 'auto',
    minWidth: '79px',
    '& + .MuiButtonBase-root.MuiTab-root': {
      borderRadius: '0',
      marginLeft: '2px',
    },
    '& + .MuiButtonBase-root.MuiTab-root + .MuiButtonBase-root.MuiTab-root': {
      borderRadius: '0 16px 16px 0',
      marginLeft: '2px',
    },

    '&.Mui-selected': {
      backgroundColor: '#6270D0',
      color: '#fff',
    },
  },
}));
// Original class: buttonsBlock
const StyledButtonsBlock = styled(Box)(theme => ({
  '& button': {
    backgroundColor: '#DCDFF4',
    color: '#656985',
    borderRadius: '16px !important',
    '&:hover': {
      backgroundColor: '#6C7AD9',
      color: '#fff',
    },
    '&:focus': {
      backgroundColor: '#7E8BE5',
      color: '#fff',
    },
    '& + button': {
      marginLeft: '8px',
    },
    '&.Mui-disabled': {
      backgroundColor: '#DCDFF4 !important',
      color: '#656985 !important',
    },
  },
}));
const UserPage = () => {
  const getLocalString = useTranslation();
  const PageState = { VIEW: 'view', EDIT: 'edit' };
  const tabsNames = { COMMON: 0, RATES: 1 };
  const pageTitle = getLocalString('AdminUsers', 'title');
  const params = useParams();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(tabsNames.COMMON);
  const [userLogin, setUserLogin] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [pageState, setPageState] = useState(PageState.VIEW);
  const [allFields, setAllFields] = useState([]);
  const [saveEvent, setSaveEvent] = useState(false);
  const navigate = useNavigate();
  const childRef = useRef();
  const formState = useRef(
    new Map([
      [PageState.VIEW, { getResponse: getUsersInfo }],
      [PageState.EDIT, { getResponse: getUserEdit }],
    ]),
  );

  function CustomTabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
      <div role='tabpanel' {...other}>
        {value === index && <Box>{children}</Box>}
      </div>
    );
  }

  useEffect(() => {
    location.pathname.includes(PageState.EDIT) && setPageState(PageState.EDIT);
  }, [PageState.EDIT]);
  useEffect(() => {
    let ignore = false;
    async function getInfo() {
      const { data: userInfoValue } = await formState.current
        .get(pageState)
        .getResponse(params.id);
      if (!ignore) {
        return userInfoValue;
      }
    }
    getInfo()
      .then(res => {
        let fields;
        pageState === PageState.VIEW &&
          (fields = res.standards.concat(res.customs));
        if (pageState === PageState.EDIT) {
          const fieldsToFormat = {};
          res.standards && (fieldsToFormat.standards = res.standards);
          res.customs && (fieldsToFormat.customs = res.customs);
          fields = formatFields(fieldsToFormat);
        }
        setAllFields(fields);
        setLoading(false);
      })
      .catch(error => console.error(error, 'ERROR IN GET USER INFO'));
    return () => {
      ignore = true;
    };
  }, [PageState.EDIT, PageState.VIEW, pageState, params.id]);
  useEffect(() => {
    if (allFields.length > 0) {
      const login = allFields.find(item => item.fieldId === 'login');
      login !== undefined && setUserLogin(login.valueId);
    }
  }, [allFields]);
  const handleButtonEvent = useCallback(
    type => {
      switch (type) {
        case ButtonsTypes.EDIT_USER:
          window.open(`/administration/users/${params.id}/edit`, '_self');
          break;
        case ButtonsTypes.SAVE:
          if (childRef.current && typeof childRef.current.save === 'function') {
            childRef.current.save();
          }
          break;
        case ButtonsTypes.CANCEL:
          window.open(`/administration/users/${params.id}`, '_self');
          break;
      }
    },
    [params.id],
  );

  return (
    <CommonPage
      buttonEvent={handleButtonEvent}
      component={'AdminUsers'}
      loading={loading}
      state={pageState}
    >
      {userLogin && (
        <Typography
          variant='h3'
          sx={{
            m: 0,
            p: 0,
            color: '#41424E',
            fontWeight: '700',
          }}
        >
          {userLogin}
        </Typography>
      )}
      <Grid
        container
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '20px',
        }}
      >
        <Grid>
          <StyledUserTabs
            value={activeTab}
            onChange={(event, newValue) => setActiveTab(newValue)}
            slotProps={{
              indicator: { sx: { display: 'none' } },
            }}
          >
            <Tab label={getLocalString('AdminUsers', 'labels', 'common')} />
            <Tab label={getLocalString('AdminUsers', 'labels', 'rates')} />
            <Tab label={getLocalString('AdminUsers', 'labels', 'competence')} />
          </StyledUserTabs>
        </Grid>
        {/*        <Grid item>
          {!isEdit ? (
            <IconButton onClick={handleEdit}>
              <ProjectEditIcon width={18} height={16} viewBox="0 0 18 16" />
            </IconButton>
          ) : (
            <StyledButtonsBlock>
              <Button variant="cutBluePrimary">
                <FormattedMessage id="save_text" defaultMessage="Save" />
              </Button>
              <Button onClick={handleEdit} variant="cutBluePrimary">
                <FormattedMessage id="cancel" defaultMessage="cancel" />
              </Button>
            </StyledButtonsBlock>
          )}
        </Grid>*/}
      </Grid>
      <CustomTabPanel value={activeTab} index={0}>
        <UserPageCommonTab
          fields={allFields}
          state={pageState}
          ref={childRef}
        ></UserPageCommonTab>
      </CustomTabPanel>
      <CustomTabPanel value={activeTab} index={1}>
        <UserPageRatesTab></UserPageRatesTab>
      </CustomTabPanel>
      <CustomTabPanel value={activeTab} index={2}>
        <Competencies componentMode={'view'}></Competencies>
      </CustomTabPanel>
    </CommonPage>
  );
};

export default UserPage;
